import { useDynamicModal } from 'components/DynamicModalProvider/useDynamicModal';
import { useCallback } from 'react';
import { DeltekVisionProvisionModal } from '../DeltekVisionProvisionModal';
import { DeltekPimProvisionModal } from '../DeltekPimProvisionModal';
import { BstProvisionModal } from '../BstProvisionModal';
import { ViewpointVistaProvisionModal } from '../ViewpointVistaProvisionModal';
import { DeltekVantagepointProvisionModal } from '../DeltekVantagepointProvisionModal';
import { DeltekAjeraProvisionModal } from '../DeltekAjeraProvisionModal';
import useProvisionErrorTableModal from './useProvisionErrorTableModal';
import SimpleMenu from 'components/SimpleMenu';
import { useBoardsQuery } from 'src/modules/BoardModule/api/queries/useBoardsQuery';
import { BakIntegrationProvisionModal } from '../BakIntegrationProvisionModal';
import CreateDraftModal from 'IntegrationModule/components/CreateDraftModal';

export const useProvisionOptionsMenu = ({
  teamId,
  tenant,
  realmId,
  hideLegacyProvisionOptions = false,
}: {
  teamId: number;
  /** Optional tenant for API calls */
  tenant?: string;
  /** Optional realmId for API calls */
  realmId?: string;
  /** When true, only show Create Draft and Provision Error options */
  hideLegacyProvisionOptions?: boolean;
}) => {
  const { openModal } = useDynamicModal();
  const { handleOpenProvisionErrorTableModal } = useProvisionErrorTableModal();

  const { getDefaultBoard } = useBoardsQuery({
    shouldFetchDefaultBoard: true,
    team_id: teamId,
    tenant,
    realmId,
  });

  const openProvisionOptionsMenu = useCallback(
    ({ target }: { target: HTMLElement }) => {
      const defaultBoard = getDefaultBoard();

      const provisionOptions = [
        { Modal: DeltekAjeraProvisionModal, label: 'Ajera On-Prem' },
        { Modal: BstProvisionModal, label: 'BST On-Prem' },
        { Modal: DeltekPimProvisionModal, label: 'PIM On-Prem' },
        {
          Modal: DeltekVantagepointProvisionModal,
          label: 'Vantagepoint On-Prem',
        },
        { Modal: DeltekVisionProvisionModal, label: 'Vision On-Prem' },
        { Modal: ViewpointVistaProvisionModal, label: 'Vista On-Prem' },
        { Modal: BakIntegrationProvisionModal, label: '.Bak Integration' },
      ].map(({ Modal, ...item }) => {
        return {
          ...item,

          tooltip: defaultBoard?.id ? '' : 'No default board available for provision',

          disableButton: !defaultBoard?.id,

          onClick: () => {
            openModal({
              modal: (args) => <Modal {...args} teamId={teamId} />,
            });
          },
        };
      });

      const createDraftOption = {
        label: 'Create Draft',
        onClick: () => {
          openModal({
            modal: (args) => (
              <CreateDraftModal {...args} teamId={teamId} tenant={tenant} realmId={realmId} />
            ),
          });
        },
      };

      const provisionErrorOption = {
        label: 'Provision Error',
        onClick: () => {
          handleOpenProvisionErrorTableModal({ teamId });
        },
      };

      const menuItems = hideLegacyProvisionOptions
        ? [createDraftOption, provisionErrorOption]
        : [createDraftOption, ...provisionOptions, provisionErrorOption];

      openModal({
        modal: ({ toggle }) => {
          return (
            <SimpleMenu
              anchorEl={target}
              items={menuItems}
              handleClose={toggle}
              transformMenu="translateX(0px) translateY(0px)"
            />
          );
        },
      });
    },
    [getDefaultBoard, handleOpenProvisionErrorTableModal, openModal, teamId],
  );

  return {
    openProvisionOptionsMenu,
  };
};
